/**
 * Pure positioning utility for floating UI (tooltip / popover).
 *
 * Given an anchor element and a content element, returns viewport
 * coordinates (`top` / `left`) plus the resolved placement. Coordinates
 * are intended to be applied with `position: fixed` so the floating
 * element sits above any `overflow: hidden` ancestor.
 *
 * Behaviour:
 *   1. Place at the requested side (top / bottom / left / right) with the
 *      cross axis centred.
 *   2. If the floating element would overflow that side, flip to the
 *      opposite side ONCE (no infinite ping-ponging).
 *   3. After flipping, shift along the cross axis so the floater stays in
 *      the viewport — same shift behaviour as Floating UI's `shift`
 *      middleware, kept tiny and dependency-free.
 *
 * Coordinates are clamped to the viewport with a small `padding` so the
 * floater never sits flush against the edge.
 */

/**
 * Side the floater anchors to. `*-start` and `*-end` variants align
 * the floater's cross-axis to the matching edge of the anchor instead
 * of centring on it — `bottom-end` is what right-edge-aligned menu
 * triggers (Share Invoice button) need so the menu doesn't drift away
 * from the trigger's right edge when the viewport-clamp logic kicks in.
 */
export type Side = 'top' | 'bottom' | 'left' | 'right';

/** Cross-axis alignment of the floater relative to the anchor. */
type Align = 'start' | 'center' | 'end';
export type Placement =
  | Side
  | 'bottom-start'
  | 'bottom-end'
  | 'top-start'
  | 'top-end';

export interface PositionInput {
  anchor: DOMRect;
  content: DOMRect;
  placement?: Placement;
  /** Distance (in px) between anchor and floater. */
  offset?: number;
  /** Inset from viewport edges before clamping. */
  padding?: number;
}

export interface PositionResult {
  top: number;
  left: number;
  /** Resolved SIDE (not the alignment) — top / bottom / left / right.
   *  Consumers that rely on this to render an arrow read the side only,
   *  so alignment variants collapse to their parent side. */
  placement: Side;
  /**
   * Cross-axis position (px) of the anchor's CENTER, in the floating
   * element's own local coordinate space (i.e. relative to the floater's
   * top-left corner, not the viewport) — for `top`/`bottom` sides this is
   * an X offset; for `left`/`right` sides it's a Y offset. An arrow/caret
   * should center itself on this point so it visually points at the
   * anchor's middle even when `align` is `start`/`end` (a plain
   * `left-1/2` CSS center only lines up with the anchor for `align:
   * 'center'` — `bottom-end`, for instance, right-aligns the floater to
   * the anchor, so the floater's own midpoint sits nowhere near the
   * anchor). Clamped away from the floater's edges (`ARROW_EDGE_MARGIN_PX`)
   * so the arrow never pokes out past a rounded corner.
   *
   * Optional — only `Popover` (which renders an arrow) reads this; every
   * OTHER consumer of `PositionResult` (Select, MultiSelect, Tooltip) has
   * no arrow and seeds its initial state without it. `computePosition`
   * itself always populates it.
   */
  arrowOffset?: number;
}

/** Default distance (px) between anchor and floating element. */
const DEFAULT_FLOATING_OFFSET_PX = 8;

/** Default inset (px) from viewport edges before clamping. */
const DEFAULT_VIEWPORT_PADDING_PX = 6;

/** Minimum distance (px) the arrow's center is kept from either edge of
 *  the floating panel — half the arrow's own ~14px diagonal footprint,
 *  so it never overlaps the panel's rounded corners. */
const ARROW_EDGE_MARGIN_PX = 10;

function clamp(value: number, min: number, max: number): number {
  if (max < min) return (min + max) / 2;
  return Math.min(max, Math.max(min, value));
}

const OPPOSITE: Record<Side, Side> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

/** Split a Placement into its side + optional alignment. Bare sides
 *  (no dash) map to `align: 'center'` — the historical default. */
function parsePlacement(p: Placement): { side: Side; align: Align } {
  if (p === 'bottom-start' || p === 'top-start') {
    return { side: p === 'bottom-start' ? 'bottom' : 'top', align: 'start' };
  }
  if (p === 'bottom-end' || p === 'top-end') {
    return { side: p === 'bottom-end' ? 'bottom' : 'top', align: 'end' };
  }
  return { side: p, align: 'center' };
}

function placeOnSide(
  side: Side,
  align: Align,
  anchor: DOMRect,
  content: DOMRect,
  offset: number
): { top: number; left: number } {
  switch (side) {
    case 'top':
      return {
        top: anchor.top - content.height - offset,
        left: alignHorizontal(align, anchor, content),
      };
    case 'bottom':
      return {
        top: anchor.bottom + offset,
        left: alignHorizontal(align, anchor, content),
      };
    case 'left':
      return {
        top: anchor.top + (anchor.height - content.height) / 2,
        left: anchor.left - content.width - offset,
      };
    case 'right':
      return {
        top: anchor.top + (anchor.height - content.height) / 2,
        left: anchor.right + offset,
      };
  }
}

/** Compute the floater's `left` for a top/bottom side based on alignment.
 *
 *   • `start`  — floater's LEFT edge aligned with anchor's left edge.
 *   • `center` — floater is centred on anchor (legacy default).
 *   • `end`    — floater's RIGHT edge aligned with anchor's right edge.
 */
function alignHorizontal(
  align: Align,
  anchor: DOMRect,
  content: DOMRect,
): number {
  if (align === 'start') return anchor.left;
  if (align === 'end') return anchor.right - content.width;
  return anchor.left + (anchor.width - content.width) / 2;
}

function fitsInViewport(
  side: Side,
  pos: { top: number; left: number },
  content: DOMRect,
  vw: number,
  vh: number,
  padding: number
): boolean {
  switch (side) {
    case 'top':
      return pos.top >= padding;
    case 'bottom':
      return pos.top + content.height <= vh - padding;
    case 'left':
      return pos.left >= padding;
    case 'right':
      return pos.left + content.width <= vw - padding;
  }
}

export function computePosition(input: PositionInput): PositionResult {
  const placement = input.placement ?? 'top';
  const offset = input.offset ?? DEFAULT_FLOATING_OFFSET_PX;
  const padding = input.padding ?? DEFAULT_VIEWPORT_PADDING_PX;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const { side: requestedSide, align } = parsePlacement(placement);

  // Step 1: try the requested side.
  let side: Side = requestedSide;
  let pos = placeOnSide(side, align, input.anchor, input.content, offset);

  // Step 2: flip if it does not fit. Alignment is preserved across the
  // flip — `bottom-end` flips to `top-end`, not `top` (center).
  if (!fitsInViewport(side, pos, input.content, vw, vh, padding)) {
    const flipped = OPPOSITE[side];
    const flippedPos = placeOnSide(flipped, align, input.anchor, input.content, offset);
    if (fitsInViewport(flipped, flippedPos, input.content, vw, vh, padding)) {
      side = flipped;
      pos = flippedPos;
    }
  }

  // Step 3: shift along the cross axis so the floater stays in viewport.
  // For aligned placements (`bottom-end`, etc.) the shift can still
  // nudge them inward if the trigger sits flush against the viewport
  // edge — preferred over going off-screen.
  if (side === 'top' || side === 'bottom') {
    if (pos.left < padding) pos.left = padding;
    const overflowRight = pos.left + input.content.width - (vw - padding);
    if (overflowRight > 0) pos.left -= overflowRight;
  } else {
    if (pos.top < padding) pos.top = padding;
    const overflowBottom = pos.top + input.content.height - (vh - padding);
    if (overflowBottom > 0) pos.top -= overflowBottom;
  }

  // The anchor's center, translated into the floater's own local
  // coordinate space, then clamped so an arrow centered on it never
  // pokes out past the panel's rounded corners — see `arrowOffset` doc.
  const arrowOffset =
    side === 'top' || side === 'bottom'
      ? clamp(
          input.anchor.left + input.anchor.width / 2 - pos.left,
          ARROW_EDGE_MARGIN_PX,
          input.content.width - ARROW_EDGE_MARGIN_PX,
        )
      : clamp(
          input.anchor.top + input.anchor.height / 2 - pos.top,
          ARROW_EDGE_MARGIN_PX,
          input.content.height - ARROW_EDGE_MARGIN_PX,
        );

  return {
    top: Math.round(pos.top),
    left: Math.round(pos.left),
    placement: side,
    arrowOffset: Math.round(arrowOffset),
  };
}
